import type React from "react";
import { useState, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { Upload, X, Loader2, FileText, HelpCircle, Files } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ALLOWED_FILE_TYPES } from "@/utils/constants";

import { motion } from "framer-motion";
import { GoogleDriveFiles } from "@/components/google-drive";
import PdfIcon from "@/assets/icons/pdf.svg";
import DocxIcon from "@/assets/icons/docx.png";
import { formatFileSize } from "@/utils/useful";
import { toast } from "react-toastify";
import { NicheInput } from "../../../components/select-niche";
import {
  useGlobalStoreActions,
  useInitialNiches,
  useNiches,
  usePersonalizationStatus,
} from "@/store/global-store";
import { ApiService } from "@/services/api-service";
import { Skeleton } from "@/components/ui/skeleton";
import { freeLimits, usePremium } from "@/hooks/usePremium";
import { createFileRoute } from "@tanstack/react-router";
import { useUser } from "@/store/useAuthStore";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
} from "@/components/ui/tooltip";

interface FileWithStatus {
  file: File;
  id: string;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
}

// TODO: fix bug of enable analyze writing button when only google files added

export const Route = createFileRoute("/app/_layout/personalization")({
  component: Personalization,
});

function Personalization() {
  const [fileList, setFileList] = useState<FileWithStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPremiumError, setIsPremiumError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toggleSubscriptionModal, updatePersonalizationStatus } =
    useGlobalStoreActions();
  const queryClient = useQueryClient();

  const [respMsg, setRespMsg] = useState<string | null>(null);

  const extractPatternsMutation = useMutation({
    mutationFn: ApiService.extractWritingPatterns,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      if (fileList.length > 0) {
        updatePersonalizationStatus("in_progress");
      }
      if (fileList.length > 0 && niches === initialNiches) {
        setRespMsg("Running personalization based on uploaded file(s).");
      } else if (fileList.length === 0 && niches !== initialNiches) {
        setRespMsg("Your niches has been updated");
      } else if (fileList.length > 0 && niches !== initialNiches) {
        setRespMsg(
          "Running personalization based on uploaded file(s) and selected niche(s)."
        );
      } else {
        setRespMsg(null);
      }

      setFileList([]);
      setErrorMessage(null);
      setIsPremiumError(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (error: any) => {
      console.error("Error extracting writing patterns:", error);

      let message = "Error occurred extracting writing patterns";
      let isPremium = false;

      try {
        // TODO: use the new mode to handle error
        if (error.response?.data?.detail) {
          message = error.response.data.detail;
          // Check if it's a premium-related error
          isPremium =
            message.includes("Premium") || message.includes("upgrade");
        }
      } catch (e) {
        console.error("Error parsing error response:", e);
      }

      // Set the error message and premium flag
      setErrorMessage(message);
      setIsPremiumError(isPremium);

      // Still show a toast for immediate feedback
      toast.error(message);
    },
  });
  const { hasAccess } = usePremium();

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        return {
          valid: false,
          error:
            "Invalid file type. Only PDF, DOCX, and text files are allowed.",
        };
      }

      return { valid: true };
    },
    []
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const newFiles: FileWithStatus[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = validateFile(file);

        newFiles.push({
          file,
          id: uuidv4(),
          status: validation.valid ? "pending" : "error",
          progress: 0,
          error: validation.error,
        });
      }

      setFileList((prev) => [...prev, ...newFiles]);
      // Clear any previous errors when new files are added
      setErrorMessage(null);
      setIsPremiumError(false);
    },
    [validateFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      handleFiles(droppedFiles);
    },
    [handleFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleFiles]
  );

  const removeFile = (id: string) => {
    setFileList((prev) => {
      const fileToRemove = prev.find((f) => f.id === id);
      if (!fileToRemove) return prev;

      return prev.filter((f) => f.id !== id);
    });
  };

  const clearAllFiles = () => {
    setFileList([]);

    setErrorMessage(null);
    setIsPremiumError(false);
  };

  const personalizationStatus = usePersonalizationStatus();
  const niches = useNiches();
  const initialNiches = useInitialNiches();
  const handleAnalyze = () => {
    if (niches.length === 0 && fileList.length == 0) {
      toast.error("no data attached");
      return;
    }

    const formData = new FormData();

    const totalFileSize = fileList.reduce((total, f) => total + f.file.size, 0);
    if (!hasAccess("total-file-size-limit-context", totalFileSize)) {
      setErrorMessage(
        `Total file size limit exceeded max: ${
          freeLimits["total-file-size-limit-context"] / 1024 / 1024
        }mb`
      );
      setIsPremiumError(true);
      toast.error(
        `Total file size limit exceeded max: ${
          freeLimits["total-file-size-limit-context"] / 1024 / 1024
        }mb`
      );
      return;
    }

    if (personalizationStatus === "in_progress") {
      setErrorMessage(
        "There's an ongoing personalization, please try again later"
      );
      toast.error("There's an ongoing personalization, please try again later");
      return;
    }

    for (const file of fileList) {
      formData.append("documents", file.file);
    }

    for (const niche of niches) {
      formData.append("user_writing_niche", niche);
    }

    extractPatternsMutation.mutate(formData);
  };

  const [selectedGoogleDriveFiles, setSelectedGoogleDriveFiles] = useState<
    { id: string; name: string; file: File | undefined }[]
  >([]);

  const handleGoogleDriveFileSelect = (
    fileId: string,
    fileName: string,
    myFile: File | undefined
  ) => {
    if (!selectedGoogleDriveFiles.some((file) => file.id === fileId)) {
      setSelectedGoogleDriveFiles([
        ...selectedGoogleDriveFiles,
        { id: fileId, name: fileName, file: myFile },
      ]);
    }
  };

  const removeSelectedFile = (fileId: string) => {
    setSelectedGoogleDriveFiles(
      selectedGoogleDriveFiles.filter((file) => file.id != fileId)
    );
  };

  const user = useUser();

  const { data: writingExists, isLoading } = useQuery({
    queryKey: ["has-writing-pattern"],
    queryFn: ApiService.hasWritingPatterns,
  });

  const { data } = useQuery({
    queryKey: ["user"],
    queryFn: ApiService.getUser,
  });

  const renderWritingStatusMessage = () => {
    if (writingExists) {
      return (
        <Alert className="mb-4 bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 ">
          <AlertDescription className="font-satoshi">
            We've already gotten a feel for your style. Uploading more documents
            can help us tailor things even better.
          </AlertDescription>
        </Alert>
      );
    } else {
      return (
        <Alert className="mb-4 bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300">
          <AlertDescription className="font-satoshi">
            Nothing here yet. Upload a writing sample to unlock personalized
            features.
          </AlertDescription>
        </Alert>
      );
    }
  };

  return (
    <>
      <motion.div
        className="w-full max-w-4xl mx-auto p-6 shadow-2xl rounded-2xl bg-white dark:bg-neutral-950 border dark:text-white dark:border-neutral-800 overflow-y-auto cute-scrollbar"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
          Personalization
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant={"ghost"}>
                  <HelpCircle />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  upload past writing so Inkriv can learn your style and write
                  like you.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h1>
        <div className="mb-4">
          {isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : data?.user_profile.is_personalizing ? (
            <Alert className="mb-4 bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 ">
              <AlertDescription className="font-satoshi">
                We're working on your personalized experience... Hang tight
              </AlertDescription>
            </Alert>
          ) : (
            renderWritingStatusMessage()
          )}
        </div>

        {/* Error Message Display */}
        {errorMessage && (
          <Alert
            className={cn(
              "mb-4",
              isPremiumError
                ? "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300"
                : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
            )}
          >
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-xs font-satoshi">{errorMessage}</span>
              {isPremiumError && (
                <Button
                  size="sm"
                  onClick={() => toggleSubscriptionModal(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-700 dark:hover:bg-amber-600 dark:text-white text-xs"
                >
                  Upgrade to Premium
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {respMsg && (
          <Alert
            className={
              "mb-4 bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
            }
          >
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>{respMsg}</span>
            </AlertDescription>
          </Alert>
        )}

        {/* Drag & Drop Area */}
        <p className=" text-neutral-900 dark:text-neutral-300 font-normal mb-3">
          Upload your past content to personalize Inkriv to your unique writing
          style
        </p>
        <div className="border-t-2 border-l-2 border-r-2 border-dashed rounded-lg p-8 flex flex-col justify-center bg-neutral-50 dark:bg-neutral-950">
          <div
            className={cn(
              " text-center transition-colors",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-gray-300 hover:border-primary"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-sm font-medium mb-2">
              {isDragging ? "Drop files here" : "Drag & drop files here"}
            </h3>
            <p className="text-xs text-muted-foreground">
              We accept PDF DOCX & TEXT
            </p>
            <p className="text-sm text-secondary-900 font-bold mb-4">
              Upload file
            </p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileInputChange}
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple
            />
          </div>
          <div className=" mx-auto">
            <GoogleDriveFiles onFileSelect={handleGoogleDriveFileSelect} />
          </div>
          {(fileList.length > 0 || selectedGoogleDriveFiles.length > 0) && (
            <div className="mt-4">
              <div className="flex justify-between mb-3 ">
                <h4 className="text-xs font-medium mb-2">Attached Files:</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFiles}
                  className="border shadow-none"
                >
                  Clear All
                </Button>
              </div>

              <div className="max-h-40 overflow-y-auto cute-scrollbar ">
                <ul className="gap-2 grid sm:grid-cols-2">
                  {fileList.map((file, index) => (
                    <li
                      key={`upload-${index}`}
                      className="bg-neutral-25 p-2 rounded-2xl"
                    >
                      <div className="flex items-center justify-between rounded text-xs mb-2 gap-x-2 ">
                        <div className="flex items-center">
                          {file.file.name.endsWith(".pdf") ? (
                            <img
                              src={PdfIcon || "/placeholder.svg"}
                              alt="PDF"
                              className="h-4 w-4 mr-2"
                            />
                          ) : file.file.name.endsWith(".docx") ||
                            file.file.name.endsWith(".doc") ? (
                            <img
                              src={DocxIcon || "/placeholder.svg"}
                              alt="DOCX"
                              className="h-4 w-4 mr-2"
                            />
                          ) : (
                            <FileText className="h-4 w-4 mr-2 text-neutral-900" />
                          )}
                          <span className="truncate">
                            {file.file.name.length > 30
                              ? `${file.file.name.substring(0, 20)}...`
                              : file.file.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(file.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-x-2 flex-nowrap text-xs text-neutral-600">
                        <span>{formatFileSize(file.file.size)}</span>
                      </div>
                    </li>
                  ))}
                  {selectedGoogleDriveFiles.map((file) => (
                    <li
                      key={`gdrive-${file.id}`}
                      className="bg-neutral-25 p-2 rounded-2xl"
                    >
                      <div className="flex items-center justify-between rounded text-xs mb-2 gap-x-2 ">
                        <div className="flex items-center">
                          {file.name.endsWith(".pdf") ? (
                            <img
                              src={PdfIcon || "/placeholder.svg"}
                              alt="PDF"
                              className="h-3 w-3 mr-2"
                            />
                          ) : file.name.endsWith(".docx") ||
                            file.name.endsWith(".doc") ? (
                            <img
                              src={DocxIcon || "/placeholder.svg"}
                              alt="DOCX"
                              className="h-3 w-3 mr-2"
                            />
                          ) : (
                            <FileText className="h-3 w-3 mr-2 text-neutral-900" />
                          )}
                          <span className="truncate">
                            {file.name.length > 30
                              ? `${file.name.substring(0, 20)}...`
                              : file.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSelectedFile(file.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-x-2 flex-nowrap text-xs text-neutral-600">
                        <span>
                          {formatFileSize((file.file?.size as number) || 0)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
        {!user?.user_profile?.is_premium && (
          <p className="text-xss bg-secondary-700 text-white w-full rounded-b-2xl text-center p-2">
            <>
              Upload up to {freeLimits["initial-personalization-max-documents"]}{" "}
              docs (1MB max) to get started. Need more room?{" "}
              <span
                className="text-warning-500 underline cursor-pointer"
                onClick={() => toggleSubscriptionModal(true)}
              >
                Upgrade
              </span>{" "}
              for unlimited uploads and deeper insights
            </>
          </p>
        )}

        {/* Error Messages for individual files */}
        {fileList.some((f) => f.status === "error") && (
          <div className="mb-6 mt-4 space-y-2">
            {fileList
              .filter((f) => f.status === "error" && f.error)
              .map((f) => (
                <Alert key={f.id} variant="destructive">
                  <AlertDescription>
                    {f.file.name}: {f.error}
                  </AlertDescription>
                </Alert>
              ))}
          </div>
        )}

        <NicheInput />

        {/* Action Buttons */}
        <div className="flex space-x-3 justify-between text-sm">
          <Button
            onClick={handleAnalyze}
            disabled={
              initialNiches === niches &&
              !fileList.some((f) => f.status === "pending")
            }
            className={` text-xs py-6 bg-transparent dark:text-white  ${
              !fileList.some((f) => f.status === "pending") ||
              extractPatternsMutation.isPending
                ? "text-neutral-800 hover:bg-neutral-50 border border-neutral-200"
                : "bg-secondary-700 text-white hover:text-neutral-50 hover:bg-secondary-900 transition-all duration-200"
            }`}
          >
            {extractPatternsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Analyze my writing"
            )}
          </Button>
        </div>
      </motion.div>
    </>
  );
}
